import { FolderGit2Icon } from "lucide-react";
import type { AnimationEvent, ReactNode } from "react";

import { formatWorktreePathForDisplay } from "../worktreeCleanup";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./ui/tooltip";

/**
 * Thread row status indicators, adopted from upstream's
 * `ThreadStatusIndicators.tsx`.
 *
 * Forge's sidebar row already carries its own status pill, PR and terminal
 * indicators (see `Sidebar.logic.ts` and `SidebarThreadRow.helpers.tsx`); this
 * module carries the upstream pieces forge's rows were missing: the worktree
 * indicator and the pulse synchronization. The PR-link badge machinery stays
 * with upstream's pull-request model and lands with the pull-request suite
 * ticket instead of forking forge's native PR state here.
 *
 * @module
 */

/** The thread facts the worktree indicator needs. */
export interface ThreadWorktreeIndicatorThread {
  readonly id: string;
  readonly branch?: string | null;
  readonly worktreePath?: string | null;
}

/**
 * The worktree glyph on a thread row: a folder-git icon naming the worktree
 * (and the branch it carries) on hover. Renders nothing for a thread without
 * a worktree path, which is most of them; the icon only earns its place when
 * "where does this run" has a real answer.
 */
export function ThreadWorktreeIndicator(props: {
  readonly thread: ThreadWorktreeIndicatorThread;
}): ReactNode {
  const worktreePath = props.thread.worktreePath?.trim();
  if (!worktreePath) {
    return null;
  }

  const displayPath = formatWorktreePathForDisplay(worktreePath);
  const tooltip = props.thread.branch
    ? `Worktree: ${displayPath} (${props.thread.branch})`
    : `Worktree: ${displayPath}`;

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            role="img"
            aria-label={tooltip}
            data-testid={`thread-worktree-${props.thread.id}`}
            className="inline-flex items-center justify-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            tabIndex={0}
          />
        }
      >
        <FolderGit2Icon className="size-3 text-muted-foreground/40" />
      </TooltipTrigger>
      <TooltipPopup side="top">{tooltip}</TooltipPopup>
    </Tooltip>
  );
}

/**
 * Align newly started pulses with the document clock without a timer or frame
 * loop. CSS starts each animation when the element mounts, so a terminal icon
 * appearing mid-second runs visibly out of phase with every other pulsing row;
 * resetting `startTime` to zero on the first frame snaps them together.
 */
export function synchronizeTerminalPulse(event: AnimationEvent<SVGSVGElement>): void {
  if (event.animationName !== "status-pulse") return;

  for (const animation of event.currentTarget.getAnimations()) {
    if ("animationName" in animation && animation.animationName === "status-pulse") {
      animation.startTime = 0;
    }
  }
}
